import { useMemberTimeZone } from '../MemberTimeZoneContext'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, CalendarPlus } from 'lucide-react'
import { parseISO } from 'date-fns'
import { useOutletContext } from 'react-router-dom'
import { useMemberActivitiesApi } from '../api/activities'
import { filterCancelledSessions } from '../../utils/activityFilters'

function formatSessionDate(value, locale, timeZone, memberText) {
  if (!value) return memberText("portalPresentation.member_no_upcoming_sessions")
  try {
    const date = parseISO(value)
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short', ...(timeZone ? { timeZone } : {}) }).format(date)
  } catch {
    return value
  }
}

export function buildActivityListing(activity) {
  const hasSessionList = Array.isArray(activity?.sessions)
  const visibleSessions = hasSessionList ? filterCancelledSessions(activity.sessions) : []
  const nextSession = hasSessionList
    ? visibleSessions.find((session) => session?.starts_at || session?.start_at)
    : null
  const nextSessionStartsAt = hasSessionList
    ? nextSession?.starts_at || nextSession?.start_at || null
    : activity?.next_session_starts_at || null
  const upcomingSessionsCount = hasSessionList
    ? visibleSessions.length
    : Number.isFinite(activity?.upcoming_sessions_count)
      ? activity.upcoming_sessions_count
      : 0

  return { nextSessionStartsAt, upcomingSessionsCount }
}

export default function MemberActivitiesPage({ clubCode }) {
  const clubTimeZone = useMemberTimeZone()
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const { branding } = useOutletContext() || {}
  const { listActivities } = useMemberActivitiesApi()
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError('')
      try {
        const data = await listActivities(clubCode)
        if (!cancelled) {
          setActivities(Array.isArray(data) ? data : [])
        }
      } catch (err) {
        if (!cancelled) {
          setError("portalPresentation.member_unable_to_load_activities")
          setActivities([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [clubCode, listActivities])

  const activityCards = useMemo(
    () =>
      activities.map((activity) => {
        const { nextSessionStartsAt, upcomingSessionsCount } = buildActivityListing(activity)
        return { ...activity, nextSessionStartsAt, upcomingSessionsCount }
      }),
    [activities],
  )

  return (
    <div className="mp-panel">
      <div className="mp-panel__header">
        <div>
          <p className="mp-eyebrow">{memberText("portalPresentation.member_activities")}</p>
          <h4 className="mp-panel__title">{memberText("portalPresentation.member_discover_club_programming")}</h4>
        </div>
      </div>

      {loading ? (
        <div className="mp-panel__body">{memberText("portalPresentation.member_loading_activities")}</div>
      ) : error ? (
        <div className="mp-panel__body">{memberText(error)}</div>
      ) : activityCards.length === 0 ? (
        <div className="mp-panel__body">
          <div className="mp-empty">{memberText("portalPresentation.member_no_activities_are_available_right_now")}</div>
        </div>
      ) : (
        <div className="mp-activity-grid">
          {activityCards.map((activity) => (
            <Link
              key={activity.id}
              className="mp-activity-card"
              to={`/${clubCode}/activities/${activity.id}`}
            >
              <div className="mp-activity-card__background" />
              <div className="mp-activity-card__content">
                <div>
                  <p className="mp-activity-card__label">{memberText("portalPresentation.member_activity")}</p>
                  <p className="mp-activity-card__title">{activity.name}</p>
                  <p className="mp-activity-card__meta">
                    <Calendar size={14} /> {formatSessionDate(activity.nextSessionStartsAt, locale, clubTimeZone || branding?.timeZone, memberText)}
                  </p>
                  <p className="mp-activity-card__meta">
                    {memberText(activity.upcomingSessionsCount === 1 ? "portalPresentation.member__count_upcoming_session" : "portalPresentation.member__count_upcoming_sessions", { count: activity.upcomingSessionsCount })}
                  </p>
                </div>
                <div className="mp-activity-card__action">
                  <CalendarPlus size={14} /> {memberText("portalPresentation.member_view_sessions")} </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
